/** 部署新版本偵測提示（基底元件·MainLayout 與 AIChatLayout 皆掛）。
 *
 * 啟動時記下 /version.json 的 version；之後每 5 分鐘與視窗重新取得焦點時重讀，
 * 版本不同 → 頂端顯示「系統已更新，請重新整理」+ 重新整理鈕（手動 reload，不自動）。
 * 永不自動 reload，故聊天串流 / 未儲存表單不受影響（規格 §2.2）。
 * 讀取失敗（dev 無 version.json）靜默略過，不顯示。
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Collapse } from '@mui/material';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';

const POLL_MS = 5 * 60 * 1000;   // 每 5 分鐘

async function fetchVersion(): Promise<string | null> {
  try {
    const r = await fetch('/version.json', { cache: 'no-store' });
    if (!r.ok) return null;
    const j = await r.json();
    return (j && typeof j.version === 'string') ? j.version : null;
  } catch {
    return null;   // silent ok: 無 version.json（dev）或網路暫斷，不顯示提示
  }
}

const UpdateBanner: React.FC = () => {
  const { t } = useTranslation();
  const initialRef = React.useRef<string | null>(null);
  const [outdated, setOutdated] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    const check = async () => {
      const v = await fetchVersion();
      if (!alive || v === null) return;
      if (initialRef.current === null) { initialRef.current = v; return; }
      if (v !== initialRef.current) setOutdated(true);
    };
    check();   // 記下啟動版本
    const timer = window.setInterval(check, POLL_MS);
    const onFocus = () => { check(); };
    window.addEventListener('focus', onFocus);
    return () => { alive = false; window.clearInterval(timer); window.removeEventListener('focus', onFocus); };
  }, []);

  return (
    <Collapse in={outdated} unmountOnExit>
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5,
        px: 2, py: 1, bgcolor: 'warning.main', color: 'warning.contrastText',
        fontSize: 14, position: 'relative', zIndex: (th) => th.zIndex.appBar + 2,
      }}>
        <SystemUpdateAltIcon fontSize="small" />
        <span>{t('app.updateAvailable', '系統已更新，請重新整理頁面以使用最新版本')}</span>
        <Button size="small" variant="contained" color="inherit"
          onClick={() => window.location.reload()}
          sx={{ color: 'warning.main', bgcolor: 'background.paper', '&:hover': { bgcolor: 'background.paper' } }}>
          {t('app.refresh', '重新整理')}
        </Button>
      </Box>
    </Collapse>
  );
};

export default UpdateBanner;
